import { useState } from "react";
import { Check, Loader2, Sprout, X, AlertCircle, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { applyChatPlan, formatApiError } from "@/lib/api";

/**
 * Inline plan card rendered inside the rooteed chat.
 *
 * When the LLM reply contains a ``json-plan`` fenced block, the backend
 * parses it and returns it as `message.plan`. We render a sticky card with
 * the plan summary + a checklist of proposed actions and a single "Apply"
 * CTA. Apply hits POST /api/ai-plans/apply and then — on success —
 * refreshes the surrounding garden context so Beds/Crops/Dashboard
 * reflect the change immediately.
 *
 * The component is stateful about its lifecycle:
 *   idle  → awaiting user action
 *   busy  → Apply is in flight
 *   done  → results received; we display per-action success ticks
 *   cancelled → user tapped Cancel, card collapses
 */
export default function ChatPlanCard({ plan, onApplied, onCancel }) {
    const [state, setState] = useState("idle"); // idle | busy | done | cancelled
    const [results, setResults] = useState(null);

    if (!plan) return null;
    const actions = plan.actions || [];

    const apply = async () => {
        setState("busy");
        try {
            const res = await applyChatPlan({ plan, garden_id: plan.garden_id });
            setResults(res);
            setState("done");
            const ok = (res.results || []).filter(r => r.ok).length;
            const failed = (res.results || []).length - ok;
            if (failed === 0) {
                toast.success(`Plan applied — ${ok} change${ok === 1 ? "" : "s"} live in your garden.`);
            } else {
                toast.message(`Applied ${ok}/${ok + failed} actions — ${failed} didn't take.`);
            }
            onApplied?.(res);
        } catch (err) {
            toast.error(formatApiError(err));
            setState("idle");
        }
    };

    const cancel = () => {
        setState("cancelled");
        onCancel?.();
    };

    if (state === "cancelled") {
        return (
            <div
                className="mt-2 text-xs text-[var(--vm-text-muted)] italic"
                data-testid="plan-card-cancelled"
            >
                Plan dismissed — no changes were made.
            </div>
        );
    }

    const borderClass = state === "done"
        ? "border-[var(--vm-sage)]/60"
        : "border-[var(--vm-accent)]/40";

    return (
        <div
            className={`mt-3 rounded-xl border ${borderClass} bg-[var(--vm-bg)]/70 p-4 shadow-sm`}
            data-testid="chat-plan-card"
        >
            <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[var(--vm-accent)]/15 flex items-center justify-center shrink-0">
                    <Sprout className="w-4 h-4 text-[var(--vm-accent)]" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-[0.18em] text-[var(--vm-sage)] mb-1">
                        {state === "done" ? "Applied" : "Proposed plan"}
                    </div>
                    <div className="text-sm text-[var(--vm-text)] leading-snug" data-testid="plan-summary">
                        {plan.summary || "rooteed has a plan ready for you."}
                    </div>
                </div>
            </div>

            <ul className="mt-3 space-y-1.5" data-testid="plan-actions">
                {actions.map((a, i) => {
                    const result = results?.results?.[i];
                    let icon, text;
                    if (state === "done" && result) {
                        icon = result.ok
                            ? <CheckCircle2 className="w-3.5 h-3.5 text-[var(--vm-sage)] shrink-0 mt-0.5" />
                            : <AlertCircle className="w-3.5 h-3.5 text-[var(--vm-secondary)] shrink-0 mt-0.5" />;
                        text = result.ok
                            ? (a._description || result.description)
                            : `${a._description || result.description}${result.error ? ` — ${result.error}` : ""}`;
                    } else {
                        icon = <span className="inline-block w-3.5 h-3.5 rounded-full border border-[var(--vm-border)] shrink-0 mt-0.5" />;
                        text = a._description || a.kind || "Action";
                    }
                    return (
                        <li
                            key={i}
                            data-testid={`plan-action-${i}`}
                            data-state={state === "done" ? (result?.ok ? "ok" : "failed") : "pending"}
                            className="flex items-start gap-2 text-xs text-[var(--vm-text-secondary)] leading-snug"
                        >
                            {icon}
                            <span>{text}</span>
                        </li>
                    );
                })}
            </ul>

            {state !== "done" && (
                <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                        onClick={apply}
                        disabled={state === "busy"}
                        data-testid="plan-apply-btn"
                        className="rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)] h-9 px-4"
                    >
                        {state === "busy"
                            ? (<><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />Applying…</>)
                            : (<><Check className="w-3.5 h-3.5 mr-2" />Apply to my garden</>)}
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={cancel}
                        disabled={state === "busy"}
                        data-testid="plan-cancel-btn"
                        className="rounded-full text-[var(--vm-text-muted)] hover:text-[var(--vm-text)] h-9 px-4"
                    >
                        <X className="w-3.5 h-3.5 mr-2" />Dismiss
                    </Button>
                </div>
            )}

            {state === "done" && (
                <div className="mt-3 text-xs text-[var(--vm-text-muted)]">
                    Garden refreshed · Beds, Crops and Dashboard are up to date.
                </div>
            )}
        </div>
    );
}
